import { useState, useEffect } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { withNetworkRetry, isNetworkError, SEARCH_NETWORK_ERROR } from "@/lib/rpcRetry";
import { ManagerAvatar } from "@/components/ManagerAvatar";

interface Manager {
  id: string;
  name: string;
  nationality: string | null;
  slug: string;
}

interface ManagerSearchProps {
  onSelectManager: (managerId: string, managerName: string, managerSlug: string) => void;
  compact?: boolean;
  selectedManagerName?: string;
}

const ManagerSearch = ({ onSelectManager, compact = false, selectedManagerName }: ManagerSearchProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [managers, setManagers] = useState<Manager[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let cancelled = false;
    const searchManagers = async () => {
      if (searchQuery.length < 2) {
        setManagers([]);
        setShowResults(false);
        return;
      }

      setIsLoading(true);
      try {
        const { data, error } = await withNetworkRetry(() =>
          supabase.rpc("search_managers_unaccent", { search_term: searchQuery })
        );
        if (cancelled) return;
        if (error) throw error;

        setManagers(data || []);
        setShowResults(true);
      } catch (error) {
        if (cancelled) return;
        console.error("Error searching managers:", error);
        toast({
          title: "Error",
          description: isNetworkError(error) ? SEARCH_NETWORK_ERROR : "Failed to search managers",
          variant: "destructive",
        });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    const debounce = setTimeout(searchManagers, 300);
    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [searchQuery, toast]);

  const handleSelectManager = (manager: Manager) => {
    setSearchQuery("");
    setShowResults(false);
    onSelectManager(manager.id, manager.name, manager.slug);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder={selectedManagerName || (compact ? "Search manager..." : "Search for a manager (e.g., Guardiola, Klopp)...")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={cn(
            compact ? "pl-9 h-9 text-sm" : "pl-9",
            selectedManagerName && "placeholder:text-foreground placeholder:font-medium"
          )}
        />
      </div>

      {showResults && managers.length > 0 && (
        <div className="absolute z-50 w-full mt-2 bg-card border border-border rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {managers.map((manager) => (
            <button
              key={manager.id}
              onClick={() => handleSelectManager(manager)}
              className="w-full px-4 py-3 text-left hover:bg-muted transition-colors flex items-center gap-3 border-b border-border last:border-b-0"
            >
              <ManagerAvatar name={manager.name} size="md" />
              <div>
                <p className="font-medium text-foreground">{manager.name}</p>
                {manager.nationality && (
                  <p className="text-sm text-muted-foreground">{manager.nationality}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {showResults && managers.length === 0 && !isLoading && (
        <div className="absolute z-50 w-full mt-2 bg-card border border-border rounded-lg shadow-lg p-4">
          <p className="text-sm text-muted-foreground text-center">No managers found</p>
        </div>
      )}
    </div>
  );
};

export default ManagerSearch;
